import { buildQuotePdf, getQuotePdfFilename } from './quotePdf';
import { formatFrenchDateShort } from './quoteCustomer';

const SEND_EMAIL_URL =
  'https://nxvochdgdtnhbjbdckkw.supabase.co/functions/v1/send-email';

function quotePdfToBase64(appointment, quote) {
  const doc = buildQuotePdf(appointment, quote);
  const dataUri = doc.output('datauristring');
  const parts = String(dataUri).split(',');
  return parts[1] || '';
}

function toBase64Url(str) {
  const bytes = new TextEncoder().encode(str);
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function fromBase64Url(input) {
  const padded = String(input || '').replace(/-/g, '+').replace(/_/g, '/');
  const pad = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
  const binary = atob(padded + pad);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

/** Token URL : ids + email + snapshot compact (pas d’edge function). */
export function createQuoteAccessToken({
  quoteId,
  appointmentId,
  email,
  clientId = null,
  validUntil = null,
  snapshot = null,
}) {
  let exp = Date.now() + 30 * 24 * 60 * 60 * 1000;
  if (validUntil) {
    const end = new Date(`${String(validUntil).slice(0, 10)}T23:59:59`);
    if (!Number.isNaN(end.getTime())) exp = end.getTime();
  }

  return toBase64Url(
    JSON.stringify({
      q: quoteId,
      a: appointmentId,
      e: String(email || '').toLowerCase(),
      c: clientId || null,
      exp,
      s: snapshot || null,
    })
  );
}

export function decodeQuoteAccessToken(token) {
  if (!token) throw new Error('Token manquant');
  const raw = fromBase64Url(String(token).split('.')[0]);
  const payload = JSON.parse(raw);
  if (!payload?.q || !payload?.a || !payload?.e) {
    throw new Error('Token invalide');
  }
  if (payload.exp && Date.now() > Number(payload.exp)) {
    throw new Error('Ce lien de devis a expiré');
  }
  return payload;
}

/** Snapshot minimal pour afficher le devis sans appel réseau. */
export function buildQuoteEmailSnapshot(appointment, quote) {
  const serviceNode = appointment?.service || appointment?.Service || null;
  const problemNode = serviceNode?.problem || null;
  const equipmentNode = problemNode?.equipment || null;
  const categoryNode = equipmentNode?.category || null;

  const compactService = serviceNode
    ? {
        id: serviceNode.id,
        name: serviceNode.name,
        estimatedDuration: serviceNode.estimatedDuration,
        TVA: serviceNode.TVA,
        problem: problemNode
          ? {
              id: problemNode.id,
              name: problemNode.name,
              equipment: equipmentNode
                ? {
                    id: equipmentNode.id,
                    name: equipmentNode.name,
                    category: categoryNode
                      ? { id: categoryNode.id, name: categoryNode.name }
                      : null,
                  }
                : null,
            }
          : null,
      }
    : null;

  return {
    quote: {
      id: quote?.id,
      reference: quote?.reference,
      valid_until: quote?.valid_until,
      notes: quote?.notes,
      price: quote?.price,
      discount_type: quote?.discount_type,
      discount_value: quote?.discount_value,
      total_ht: quote?.total_ht,
      vat_rate: quote?.vat_rate,
      total_ttc: quote?.total_ttc,
      status: quote?.status,
      created_at: quote?.created_at,
    },
    appointment: {
      id: appointment?.id,
      reference: appointment?.reference,
      client: appointment?.client,
      isQuote: true,
      is_quote: true,
      createdAt: appointment?.created_at || appointment?.createdAt,
      created_at: appointment?.created_at || appointment?.createdAt,
      customerName: appointment?.customer_name || appointment?.customerName,
      customer_name: appointment?.customer_name || appointment?.customerName,
      customerEmail: appointment?.customer_email || appointment?.customerEmail,
      customer_email: appointment?.customer_email || appointment?.customerEmail,
      customerPhone: appointment?.customer_phone || appointment?.customerPhone,
      customer_phone: appointment?.customer_phone || appointment?.customerPhone,
      customerAddress: appointment?.customer_address || appointment?.customerAddress,
      customer_address: appointment?.customer_address || appointment?.customerAddress,
      customerLatitude: appointment?.customerLatitude ?? appointment?.customer_latitude,
      customerLongitude: appointment?.customerLongitude ?? appointment?.customer_longitude,
      notes: appointment?.notes,
      serviceType: appointment?.serviceType || appointment?.service_type || compactService?.name,
      service_type: appointment?.service_type || appointment?.serviceType || compactService?.name,
      problemName:
        appointment?.problemName ||
        appointment?.problem_name ||
        compactService?.problem?.name ||
        null,
      service: compactService,
      Service: compactService,
      siret: appointment?.siret,
      tva: appointment?.tva,
    },
  };
}

function formatValidUntilParts(validUntil) {
  const raw = `${validUntil || ''}`.slice(0, 10);
  const [y, m, d] = raw.split('-');
  if (!y || !m || !d) return { dateLabel: '', timeLabel: '23:59' };
  return { dateLabel: `${d}/${m}/${y}`, timeLabel: '23:59' };
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Envoie le devis établi via la même edge function send-email que le récap booking.
 */
export async function sendQuoteEstablishedEmail({
  quote,
  appointment,
  acceptUrl,
  includePdf = true,
}) {
  const customerName =
    appointment?.customer_name || appointment?.customerName || '';
  const email = appointment?.customer_email || appointment?.customerEmail || '';
  const subjectLabel =
    appointment?.problemName ||
    appointment?.problem_name ||
    appointment?.serviceType ||
    appointment?.service_type ||
    'votre demande';

  const requestDate = formatFrenchDateShort(
    appointment?.created_at || appointment?.createdAt || new Date().toISOString()
  );
  const { dateLabel: validDate, timeLabel: validTime } = formatValidUntilParts(
    quote?.valid_until || quote?.validUntil
  );

  const htmlContent = `
        <!DOCTYPE html>
        <html lang="fr">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Votre devis</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              line-height: 1.6;
              color: #333;
              max-width: 600px;
              margin: 0 auto;
              padding: 20px;
            }
            .header {
              background-color: #1a56db;
              color: white;
              padding: 20px;
              text-align: center;
              border-radius: 10px 10px 0 0;
            }
            .content {
              background-color: #f9fafb;
              padding: 30px;
              border-radius: 0 0 10px 10px;
              border: 1px solid #e5e7eb;
              border-top: none;
            }
            .info-block {
              background-color: #fff;
              border: 1px solid #e5e7eb;
              border-radius: 8px;
              padding: 15px;
              margin: 15px 0;
            }
            .info-block h4 {
              color: #1a56db;
              margin-top: 0;
              margin-bottom: 10px;
              border-bottom: 1px solid #e5e7eb;
              padding-bottom: 5px;
            }
            .info-block p {
              margin: 5px 0;
            }
            .button {
              display: inline-block;
              background-color: #1a56db;
              color: white;
              text-decoration: none;
              padding: 12px 24px;
              border-radius: 6px;
              margin-top: 12px;
            }
            .footer {
              margin-top: 30px;
              padding-top: 20px;
              border-top: 1px solid #e5e7eb;
              font-size: 12px;
              color: #6b7280;
              text-align: center;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>📄 Votre devis</h1>
          </div>
          <div class="content">
            <p>Monsieur / Madame,</p>
            <p>
              Le ${escapeHtml(requestDate)}, vous avez demandé un devis concernant
              <strong>${escapeHtml(subjectLabel)}</strong>
              ${quote?.reference ? ` (réf. <strong>${escapeHtml(quote.reference)}</strong>)` : ''}.
            </p>
            <p>
              Nous avons le plaisir de vous transmettre votre devis, que vous trouverez en pièce jointe.
            </p>

            <div class="info-block">
              <h4>Montant proposé</h4>
              <p><strong>Total HT :</strong> ${Number(quote?.total_ht || 0).toFixed(2)} €</p>
              <p><strong>TVA (${quote?.vat_rate ?? 20}%) :</strong> ${(
                Number(quote?.total_ttc || 0) - Number(quote?.total_ht || 0)
              ).toFixed(2)} €</p>
              <p><strong>Total TTC :</strong> ${Number(quote?.total_ttc || 0).toFixed(2)} €</p>
              ${
                validDate
                  ? `<p><strong>Valable jusqu'au :</strong> ${escapeHtml(validDate)} à ${escapeHtml(validTime)}</p>`
                  : ''
              }
            </div>

            <p>
              Pour accepter votre devis et procéder au paiement, il vous suffit de cliquer sur le lien ci-dessous :
            </p>
            <div style="text-align: center; margin: 20px 0;">
              <a href="${acceptUrl}" class="button">
                👉 Accepter le devis et procéder au paiement
              </a>
            </div>
            <p>
              Vous pouvez l’accepter jusqu’au <strong>${escapeHtml(validDate)}</strong> à
              <strong>${escapeHtml(validTime)}</strong>. Passé ce délai, le devis expirera automatiquement.
            </p>
            <p>
              Nous restons bien entendu à votre disposition si vous avez la moindre question concernant votre intervention.
            </p>
            <p>
              Merci pour votre confiance, et à très bientôt.
            </p>
            <p>
              Cordialement,<br/>
              L’équipe Les Nouveaux Compagnons
            </p>
          </div>
          <div class="footer">
            <p>Cet email a été envoyé automatiquement, merci de ne pas y répondre.</p>
            <p>&copy; ${new Date().getFullYear()} - Tous droits réservés</p>
          </div>
        </body>
        </html>
      `;

  const emailData = {
    to: email,
    subject: `📄 Votre devis - Réf: ${quote?.reference || appointment?.reference || ''}`,
    html: htmlContent,
    cc: 'afefjobranee@gmail.com',
  };

  if (includePdf && quote) {
    try {
      const pdfBase64 = quotePdfToBase64(appointment, quote);
      if (pdfBase64) {
        emailData.attachments = [
          {
            filename: getQuotePdfFilename(quote),
            content: pdfBase64,
          },
        ];
      }
    } catch (pdfErr) {
      console.error('PDF devis non joint:', pdfErr);
    }
  }

  const response = await fetch(SEND_EMAIL_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(emailData),
  });

  if (!response.ok) {
    throw new Error(`Erreur HTTP: ${response.status}`);
  }

  console.log('✅ Email devis envoyé avec succès');
  return true;
}
